import React, { useCallback, useEffect, useState } from 'react'
import { makeStyles } from '@mui/styles'
import {
  Box,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Theme,
  Tooltip,
  Typography,
} from '@mui/material'

import ButtonComponent from '@professocial/professocialFormComponents/FormComponents/ButtonComponent'
import ActionItem from '@professocial/professocialFormComponents/FormComponents/ActionItem'
import SearchInput from '@professocial/professocialFormComponents/FormComponents/SearchInput'

import CheckIcon from './CheckIcon'

import { useTranslation } from 'react-i18next'
import {
  UserLogin,
  UserLoginSortOrder,
  UserLoginSortValue, UserSettings
} from '@professocialApi/professocial-core-frontend-api'

import { FilterParams } from '../../../screens/Editor/EditorAccounts'

import { format } from 'date-fns'
import { Template } from '../../../screens/Admin/AdminAccounts'
import { getUser } from '../../../store/common/common.selectors'

import { useDispatch, useSelector } from 'react-redux'
import TableIcon from './TableIcon'
import { NavLink } from 'react-router-dom'
import { useRouteMatch } from 'react-router'
import useHidden from '../../../util/useHidden'
import Preloader from '@professocial/professocialFormComponents/FormComponents/Preloader'
import { loginAsInitiated } from '../../../store/accounts/accounts.actions'
import { LoginAsRequest } from '@professocialApi/professocial-core-frontend-api/apis/AutorisationROApi'
// eslint-disable-next-line no-undef
import Timeout = NodeJS.Timeout;
import { ResultPageWebModel } from '@professocialApi/professocial-core-frontend-api/models'
import addTimezone from '../../../util/addTimezone'
import ConnectedPages from './ConnectedPages'
import { Mandator } from '@professocialApi/professocial-core-frontend-api/models/Mandator'

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    width: '100%',
    marginBottom: theme.spacing(7),
  },
  tableContainer: {
    marginTop: theme.spacing(1),
  },
  table: {
    width: '100%',
    overflowX: 'auto',
  },
  tableHeadCell: {
    '&:first-child': {
      paddingLeft: '24px',
    },
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: 'white',
      fontWeight: 'bold',
    },
  },
  tableBodyCell: {
    '&:first-child': {
      paddingLeft: '24px',
    },
  },
  sortLabel: {
    fontWeight: 'bold',
  },
  IconBox: {
    display: 'flex',
  },
  noAccounts: {
    padding: theme.spacing(2),
    width: '100%',
    textAlign: 'center',
  },
  pagination: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
}))

interface IAccountTableProps {
  data?: ResultPageWebModel;
  user?: UserSettings;
  pageKey: string;
  mandatorOptions?: Mandator
  filterParams: FilterParams;
  template?: Template[];
  deleteUser?: (userId: string) => void;
  csvExport?: () => void;
  setFilterSettings?: (filterParams: FilterParams) => void;
}

const AccountTable: React.FC<IAccountTableProps> = (props) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const { url } = useRouteMatch()
  const role = props.pageKey
  const isVisible = useHidden()
  const dispatch = useDispatch()

  const user: UserLogin | undefined = useSelector(getUser)

  const [search, setSearch] = useState<any>(props.filterParams.searchWith)
  const [order, setOrder] = useState<UserLoginSortOrder>(props.filterParams.sortOrder)
  const [orderBy, setOrderBy] = useState<UserLoginSortValue | undefined>(props.filterParams.sortValue || undefined)
  const [pageSize, setPageSize] = useState<number>(props.filterParams.limit)
  const [page, setPage] = useState<number>(0)
  const [timer, setTimer] = useState<Timeout>()
  const [template, setTemplate] = useState<Template[]>(props.template ? props.template : [])

  const dispatchLoginAs = useCallback((params: LoginAsRequest) => dispatch(loginAsInitiated(params)), [dispatch])

  useEffect(() => {
    props.template && setTemplate(props.template)
  }, [props.template],
  )

  useEffect(() => {
    props.setFilterSettings && props.setFilterSettings({
      limit: pageSize,
      offset: page * pageSize,
      sortOrder: order,
      sortValue: orderBy,
      searchWith: search,
    })
  }, [order, orderBy, page, pageSize],
  )

  const initiateSearch = () => {
    props.setFilterSettings && props.setFilterSettings({
      limit: pageSize,
      offset: 0,
      sortOrder: order,
      sortValue: orderBy,
      searchWith: search,
    })
    setPage(0)
  }

  const loginAs = (username: string) => {
    dispatchLoginAs({ username })
  }

  const csvExport = () => {
    props.csvExport && props.csvExport()
  }

  useEffect(() => {
    if (timer) {
      clearTimeout(timer)
    }
    setTimer(setTimeout(initiateSearch, 700))
  }, [search],
  )

  return (
    <Paper className={classes.root}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-end', sm: 'center' }}
        justifyContent="space-between"
        sx={{ pt: 4, pb: 0, px: 3 }}
      >
        <Stack direction="row" alignItems="center" spacing={3}>
          <Box><SearchInput value={search} onChangeValue={(value: string) => setSearch(value)}/></Box>
          {props.pageKey !== 'detainee' && (
            <Box>
              <NavLink to={`${url}/add_user`} style={{ textDecoration: 'none' }}>
                <ButtonComponent
                  color="primary"
                  text={t('accountsCommon:createAccount')}
                  variant="contained"
                  startIcon="add"
                  disableElevation
                />
              </NavLink>
            </Box>
          )
          }
        </Stack>
        {user && user.role === 'ADMIN' && (role === 'user' || role === 'detainee') && (
          <Box>
            <ButtonComponent
              color="primary"
              variant="outlined"
              text={t('accountsCommon:csvExport')}
              aria-label="Download"
              startIcon="input"
              disableElevation
              onClick={csvExport}
            />
          </Box>
        )}
      </Stack>
      {props.data && user ? (
        <>
          <TableContainer className={classes.tableContainer}>
            <Table className={classes.table} size="small" stickyHeader sx={{ backgroundColor: 'white' }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: 'white' }}>
                  {template && template.map((element: Template) => {
                    return (
                      <TableCell
                        className={classes.tableHeadCell}
                        size="medium"
                        align="left"
                        key={`head-${element.name}`}
                        sx={{ backgroundColor: 'white' }} // display: isVisible(element.hidden) ? 'table-cell' : 'none',
                      >
                        <Tooltip title="Sort" enterDelay={300} sx={{ backgroundColor: 'white' }}>
                          <TableSortLabel
                            active={orderBy === `${element.name}`}
                            direction={order}
                            className={classes.sortLabel}
                            onClick={() => {
                              setOrder(order === UserLoginSortOrder.Desc ? UserLoginSortOrder.Asc : UserLoginSortOrder.Desc)
                              element.sortable && setOrderBy(element.sortable)
                            }}
                          >
                            {element.label && t<string>(`accountsCommon:${element.label}`)}
                          </TableSortLabel>
                        </Tooltip>
                      </TableCell>
                    )
                  })}
                  {role === 'user' && <TableCell className={classes.tableHeadCell} size="medium" sx={{ backgroundColor: 'white' }}/>}
                  <TableCell className={classes.tableHeadCell} size="medium" align="center" key="head-actions" sx={{ backgroundColor: 'white' }}>
                    {t('accountsCommon:accountActions')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {props.data.entries?.map((row: any, index: number) => (
                  <TableRow key={`${index}-${row.id}`}>
                    {template && template.map((element: Template, i) => {
                      const regExpPoint = /[.]/g
                      let column = element.name
                      if ((element.name).search(regExpPoint) !== -1) {
                        const array = (element.name).split(regExpPoint)
                        const obj = row[array[0]]
                        column = obj ? obj[array[1]] : ''
                      } else {
                        Object.keys(row).map((col) => {
                          if ((element.name).search(col) !== -1) {
                            column = column.replace(col, row[col])
                          }
                        })
                      }
                      if (element.format) {
                        column = row[element.name] ? format(addTimezone(new Date(row[element.name] * 1000)), element.format) : ''
                      }
                      return (
                        <TableCell
                          key={`${index + i}-${row.id}-${column}`}
                          sx={{ display: isVisible(element.hidden) ? 'table-cell' : 'none' }}
                          align="left"
                          className={classes.tableBodyCell}
                        >
                          {column}
                        </TableCell>
                      )
                    })}
                    {role === 'user' && (
                      <TableCell align="right">
                        <Stack direction="row" justifyContent="center" spacing={3}>
                          <Box display="flex" justifyContent="center">
                            <Box sx={{ position: 'relative' }}>
                              <CheckIcon check={row.commitmentsConfirmed}/>
                              <TableIcon
                                title={t('accountsCommon:commitmentsConfirmed')}
                                value={row.commitmentsConfirmed}
                                icon="commitmentsConfirmed"
                              />
                            </Box>
                          </Box>
                          <Box display="flex" justifyContent="center">
                            <Box sx={{ position: 'relative' }}>
                              <CheckIcon check={row.finishedQuiz}/>
                              <TableIcon
                                title={t('accountsCommon:finishedQuiz')}
                                value={row.finishedQuiz}
                                icon="finishedQuiz"
                              />
                            </Box>
                          </Box>
                          <Box display="flex" justifyContent="center">
                            <Box sx={{ position: 'relative' }}>
                              <CheckIcon check={row.finishedWizard}/>
                              <TableIcon
                                title={t('accountsCommon:finishedWizard')}
                                value={row.finishedWizard}
                                icon="finishedWizard"
                              />
                            </Box>
                          </Box>
                        </Stack>
                      </TableCell>
                    )
                    }
                    <TableCell align="right">
                      <Stack direction="row" spacing={1}>
                        {props.user && props.user.role === 'ADMIN' && (role === 'user' || role === 'editor') && (
                          <ActionItem
                            icon="key" onClick={() => {
                              return loginAs(row.username)
                            }}
                          />
                        )
                        }
                        <NavLink to={`${url}/edit/${row.id}`}>
                          <ActionItem icon="edit"/>
                        </NavLink>
                        <ActionItem icon="delete" onClick={() => props.deleteUser && props.deleteUser(row.id)}/>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {props.data.entries?.length === 0 &&
              <Typography className={classes.noAccounts}>{t('accountsCommon:noAccounts')}</Typography>}

          <Box className={classes.pagination}>
            {props.user && <ConnectedPages mandatorOptions={props.mandatorOptions}/>}

            <TablePagination
              rowsPerPageOptions={[25, 50, 100]}
              count={props.data.entriesTotal || props.data.entriesFilteredTotal || 0}
              rowsPerPage={pageSize}
              component="div"
              page={page}
              onPageChange={(event: any, page: number) => {
                setPage(page)
              }}
              onRowsPerPageChange={(event: any) => {
                setPage(0)
                setPageSize(event.target.value)
              }}
            />
          </Box>
        </>
      )
        : <Preloader/>}
    </Paper>
  )
}
export default AccountTable
